import {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
} from 'react';

import { UseFormReturn, useFormState } from 'react-hook-form';
import Slider from 'react-slick';

import {
  AgeRange,
  Condition,
  Defects,
  RenovationPlans,
} from '@graphql/requests';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { useForm } from '@ui-furniture/common/form';
import { useToast } from '@ui-furniture/common/toast';
import { wait } from '@ui-furniture/common/utils/wait';
import { useLoadingCallback } from '@ui-furniture/hooks/use-loading-callback';
import { useFocusByIdOnMount } from '@ui-furniture/lib/hooks/use-focus-by-id-on-mount';

import { calculateRecommendedSurvey } from '../common/calculate-recommended-survey';
import { calculateSurveyPrices } from '../common/calculate-survey-prices';
import {
  createRecommendationSchema,
  CreateSurveyRecommendationData,
} from '../common/create-recommendation-schema';

const SurveyRecommendationContext = createContext<{
  firstInputId: string;
  formId: string;
  surveysId: string;
  isCalculating: boolean;
  carouselRef: RefObject<Slider>;
  form: UseFormReturn<CreateSurveyRecommendationData>;
  isFormDirty: boolean;
  ensureFormIsComplete: () => Promise<boolean>;
  recommendation: {
    recommendedSurvey: 2 | 3;
    surveyPrices: {
      2: number;
      3: number;
    };
    input: CreateSurveyRecommendationData;
  } | null;
  calculatePriceAndRecommendation: (
    data: CreateSurveyRecommendationData
  ) => void;
}>(null as any);

export const SurveyRecommendationProvider = (props: PropsWithChildren) => {
  const carouselRef = useRef<Slider>(null);
  const { reportObjective } = useAnalyticsReportingService();
  const [recommendation, setRecommendation] = useState<{
    recommendedSurvey: 2 | 3;
    surveyPrices: {
      2: number;
      3: number;
    };
    input: CreateSurveyRecommendationData;
  } | null>(null);
  const firstInputId = useFocusByIdOnMount();
  const formId = useId();
  const surveysId = useId();

  const form = useForm(createRecommendationSchema, {
    defaultValues: {
      ageRange: AgeRange.From10To50,
      condition: Condition.Good,
      defects: Defects.Cosmetic,
      renovationPlans: RenovationPlans.Cosmetic,
      bedroomCount: 3,
      specialistConstruction: [],
    },
  });

  const { showToast } = useToast();

  const [isCalculating, calculatePriceAndRecommendation] = useLoadingCallback(
    async (data: CreateSurveyRecommendationData) => {
      await wait(Math.random() * 200);
      const newRecommendation = calculateRecommendedSurvey(data);
      const surveyPrices = calculateSurveyPrices(data);
      reportObjective({
        category: 'Survey',
        name: 'Survey - Recommendation',
        stage: 'event',
        data: {
          recommendedSurvey: newRecommendation,
          level2SurveyPrice: surveyPrices[2],
          level3SurveyPrice: surveyPrices[3],
          ...data,
        },
      });
      setRecommendation({
        recommendedSurvey: newRecommendation,
        surveyPrices,
        input: data,
      });
      carouselRef.current?.slickGoTo(newRecommendation - 1);
      await wait(200);
      document
        .getElementById(surveysId)
        ?.scrollIntoView({ behavior: 'smooth' });

      form.reset(undefined, {
        keepValues: true,
      });
    },
    []
  );
  const { isDirty: isFormDirty } = useFormState({
    control: form.control,
  });

  const ensureFormIsComplete = useCallback(async () => {
    if (!recommendation || isFormDirty) {
      showToast({
        message: !recommendation
          ? 'Please fill in the form before trying to book a survey'
          : 'You have some unsaved changes in the form, please submit them before trying to book a survey',
        preventDuplicate: true,
      });
      document.getElementById(formId)?.scrollIntoView({ behavior: 'smooth' });
      document.getElementById(firstInputId)?.focus({
        preventScroll: true,
      });
      reportObjective({
        category: 'Survey',
        stage: 'event',
        name: 'Survey - Book Tried',
        data: {
          reason: !recommendation ? 'No Recommendation' : 'Dirty Form',
        },
      });
      return false;
    }
    return true;
  }, [recommendation, isFormDirty]);

  return (
    <SurveyRecommendationContext.Provider
      value={{
        firstInputId,
        formId,
        surveysId,
        carouselRef,
        form: form as any,
        isFormDirty,
        ensureFormIsComplete,
        recommendation,
        isCalculating,
        calculatePriceAndRecommendation,
      }}
      {...props}
    />
  );
};

export const useSurveyRecommendation = () => {
  const context = useContext(SurveyRecommendationContext);
  if (!context) {
    throw new Error(
      'useSurveyRecommendation must be used within a SurveyRecommendationProvider'
    );
  }
  return context;
};
